import * as React from 'react';

// TODO: Tech Debt - Refactor FilterContext to use proper generic types (ADO #91801)
// Currently uses `any` for DynamicFilters, QuickFilters index, and DynamicFiltersDisplayed.
// Ideal solution: Make useFilter<T>() generic so consumers can specify their filter type.
// Blocked by: ~15 files need updates, missing properties in filter type definitions,
// and UI components that use dynamic property access.

export interface QuickFilters {
  assignedStatus?: 'all' | 'assigned' | 'unassigned';
  // Allow dynamic keys for future quick filters
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

// Generic filter type for backward compatibility.
// The context was previously typed as `any`. We keep it as `any` to avoid
// breaking existing code that uses different filter types (TasksListFilter,
// ComplexLossFilters, etc.) with the same context.
type DynamicFilters = any;

type DynamicFiltersDisplayed = Record<string, any> | null;

export interface FilterContextValue {
  filters: DynamicFilters;
  setFilters: React.Dispatch<React.SetStateAction<DynamicFilters>>;
  quickFilters: QuickFilters;
  setQuickFilters: React.Dispatch<React.SetStateAction<QuickFilters>>;
  dynamicFiltersDisplayed: DynamicFiltersDisplayed;
  setDynamicFiltersDisplayed: React.Dispatch<
    React.SetStateAction<DynamicFiltersDisplayed>
  >;
}

export const FilterContext = React.createContext<
  FilterContextValue | undefined
>(undefined);

function FilterProvider({ children }: { children: React.ReactChild }) {
  // Note: filters starts as undefined intentionally for backward compatibility.
  // Some existing code checks `!!filters` to conditionally run logic.
  const [filters, setFilters] = React.useState<DynamicFilters>();
  const [dynamicFiltersDisplayed, setDynamicFiltersDisplayed] =
    React.useState<DynamicFiltersDisplayed>(null);
  const [quickFilters, setQuickFilters] = React.useState<QuickFilters>({});

  const value: FilterContextValue = {
    filters,
    setFilters,
    quickFilters,
    setQuickFilters,
    dynamicFiltersDisplayed,
    setDynamicFiltersDisplayed,
  };

  return (
    <FilterContext.Provider value={value}>{children}</FilterContext.Provider>
  );
}

function useFilter(): FilterContextValue {
  const context = React.useContext(FilterContext);
  if (context === undefined) {
    throw new Error('useFilter must be used within a FilterProvider');
  }
  return context;
}

export { FilterProvider, useFilter };
export type { DynamicFilters };
